import React, { useState, useEffect } from 'react';
import { Play, Eye, Calendar, User } from 'lucide-react';
import { fetchVideoMetadata } from '../services/youtubeApi';

const VideoPlayer = ({ videoId, title, onPlay, className = "", startTime = 0 }) => {
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadMetadata = async () => {
      setLoading(true);
      const data = await fetchVideoMetadata(videoId);
      setMetadata(data);
      setLoading(false);
    };

    if (videoId) {
      loadMetadata();
    }
  }, [videoId]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatViewCount = (count) => {
    if (!count) return '0';
    const num = parseInt(count);
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  const truncateDescription = (text, maxLength = 150) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className={`bg-black/20 rounded-2xl overflow-hidden ${className}`}>
      {/* Desktop Layout: Video left, metadata right */}
      <div className="hidden md:flex">
        {/* Video Player */}
        <div className="flex-1 relative aspect-video">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?controls=0&modestbranding=1&rel=0&showinfo=0${startTime > 0 ? `&start=${startTime}` : ''}`}
            title={title || 'YouTube video player'}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            className="w-full h-full"
          />
          <div className="absolute inset-0 bg-black/20 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
            <button
              onClick={() => onPlay && onPlay({ id: videoId, title: metadata?.title || title })}
              className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center"
            >
              <Play size={24} className="text-white ml-1" />
            </button>
          </div>
        </div>

        {/* Metadata Panel */}
        <div className="flex-1 p-6 flex flex-col justify-center">
          {loading ? (
            <div className="animate-pulse">
              <div className="h-6 bg-white/10 rounded mb-3"></div>
              <div className="h-4 bg-white/10 rounded mb-2"></div>
              <div className="h-4 bg-white/10 rounded mb-4"></div>
              <div className="h-3 bg-white/10 rounded mb-2"></div>
              <div className="h-3 bg-white/10 rounded"></div>
            </div>
          ) : metadata ? (
            <>
              <h3 className="text-xl font-semibold text-white mb-3 line-clamp-2">
                {metadata.title}
              </h3>
              
              <div className="flex items-center gap-4 text-sm text-white/70 mb-4">
                <div className="flex items-center gap-1">
                  <User size={14} />
                  <span>{metadata.channelTitle}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Eye size={14} />
                  <span>{formatViewCount(metadata.viewCount)} views</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar size={14} />
                  <span>{formatDate(metadata.publishedAt)}</span>
                </div>
              </div>

              <p className="text-white/80 text-sm leading-relaxed mb-4">
                {truncateDescription(metadata.description)}
              </p>

              <button
                onClick={() => onPlay && onPlay({ id: videoId, title: metadata.title })}
                className="self-start px-6 py-3 bg-white text-black font-semibold rounded-xl hover:bg-white/90 transition-colors inline-flex items-center gap-2"
              >
                <Play size={18} />
                Watch Now
              </button>
            </>
          ) : (
            <div className="text-white/60">
              <h3 className="text-xl font-semibold mb-3">{title}</h3>
              <p>Metadata unavailable</p>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Layout: Video top, metadata bottom */}
      <div className="md:hidden">
        {/* Video Player */}
        <div className="relative aspect-video">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?controls=0&modestbranding=1&rel=0&showinfo=0&playsinline=1${startTime > 0 ? `&start=${startTime}` : ''}`}
            title={title || 'YouTube video player'}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            className="w-full h-full object-cover"
            style={{ objectFit: 'cover' }}
          />
          <div className="absolute inset-0 bg-black/10 hover:bg-black/20 transition-all duration-300 flex items-center justify-center touch-manipulation">
            <button
              onClick={() => onPlay && onPlay({ id: videoId, title: metadata?.title || title })}
              className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center touch-manipulation active:scale-95 transition-transform"
            >
              <Play size={24} className="text-white ml-1" />
            </button>
          </div>
        </div>

        {/* Metadata Panel */}
        <div className="p-4">
          {loading ? (
            <div className="animate-pulse">
              <div className="h-5 bg-white/10 rounded mb-2"></div>
              <div className="h-3 bg-white/10 rounded mb-3"></div>
              <div className="h-3 bg-white/10 rounded mb-2"></div>
              <div className="h-3 bg-white/10 rounded"></div>
            </div>
          ) : metadata ? (
            <>
              <h3 className="text-lg font-semibold text-white mb-2 line-clamp-2">
                {metadata.title}
              </h3>
              
              <div className="flex items-center gap-3 text-xs text-white/70 mb-3">
                <div className="flex items-center gap-1">
                  <User size={12} />
                  <span>{metadata.channelTitle}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Eye size={12} />
                  <span>{formatViewCount(metadata.viewCount)}</span>
                </div>
              </div>

              <p className="text-white/80 text-sm leading-relaxed mb-3">
                {truncateDescription(metadata.description, 100)}
              </p>

              <button
                onClick={() => onPlay && onPlay({ id: videoId, title: metadata.title })}
                className="w-full px-4 py-2 bg-white text-black font-semibold rounded-lg hover:bg-white/90 transition-colors inline-flex items-center justify-center gap-2"
              >
                <Play size={16} />
                Watch Now
              </button>
            </>
          ) : (
            <div className="text-white/60">
              <h3 className="text-lg font-semibold mb-2">{title}</h3>
              <p className="text-sm">Metadata unavailable</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer; 